import { inject, Injectable } from "@angular/core";
import { lastValueFrom, Observable } from "rxjs";
import { DataResult, ICRUDOptions } from '@phx-erp/shared';
import { BaseListService } from "../../../@core/services/base-list.service";
import { SystemService } from "../../../@core/services/system.service";
import { DELETE_TRADING_SET_RELATION, GET_TRADING_SET_PRICE_INFO, GET_TRADING_SET_RELATION, GET_TRADING_SET_RELATIONS, SAVE_TRADING_SET_RELATION, SAVE_TRADING_SET_RELATIONS } from "../../../data/definitions/trading-set.definition";
import { QueryResult } from "../../../data/query-result";
import { BaseDataService } from "../../../data/services/base-data.service";
import { DeleteTradingSetRelationMutation, DeleteTradingSetRelationMutationVariables, DeletionResponse, GetTradingSetPriceInfoQuery, GetTradingSetPriceInfoQueryVariables, GetTradingSetRelationQuery, GetTradingSetRelationQueryVariables, GetTradingSetRelationsQuery, GetTradingSetRelationsQueryVariables, SaveTradingSetRelationMutation, SaveTradingSetRelationMutationVariables, SaveTradingSetRelationsMutation, SaveTradingSetRelationsMutationVariables, TradingSetPriceInfo, TradingSetPriceInfoSearchInput, TradingSetRelation, TradingSetRelationInput, TradingSetRelationSearchInput } from "../../../generated/generated-types";

@Injectable({
    providedIn: 'root'
})
export class TradingSetRelationService extends BaseListService<TradingSetRelation> {

    private systemService = inject(SystemService);
    constructor(
        private baseDataService: BaseDataService) {
        super();
    }

    getItems(searchInput: TradingSetRelationSearchInput, options?: ICRUDOptions): QueryResult<any, Record<string, any>> {
        let fragment = GET_TRADING_SET_RELATIONS;
        return this.baseDataService.query<GetTradingSetRelationsQuery, GetTradingSetRelationsQueryVariables>(
            fragment, {
            input: searchInput
        });
    }

    getItem(id: string) {
        return this.baseDataService.query<GetTradingSetRelationQuery, GetTradingSetRelationQueryVariables>(
            GET_TRADING_SET_RELATION, {
            id: id
        });
    }

    getTradingSetPriceInfo(tradingSetId: string, searchInput: TradingSetPriceInfoSearchInput) {
        return this.baseDataService.query<GetTradingSetPriceInfoQuery, GetTradingSetPriceInfoQueryVariables>(
            GET_TRADING_SET_PRICE_INFO, {
            tradingSetId: tradingSetId,
            searchInput: searchInput
        });
    }

    getItemStream(id: string) {
        return this.getItem(id).mapStream(x => x.getTradingSetRelation);
    }

    async getItemData(id: string): Promise<TradingSetRelation> {
        let l = await this.getItem(id).mapSingle(x => x.getTradingSetRelation)
        return await lastValueFrom(l) as TradingSetRelation;
    }

    async getTradingSetPriceInfoData(tradingSetId: string, searchInput: TradingSetPriceInfoSearchInput): Promise<TradingSetPriceInfo[]> {
        let l = await this.getTradingSetPriceInfo(tradingSetId, searchInput).mapSingle(x => x.getTradingSetPriceInfo)
        return await lastValueFrom(l) as TradingSetPriceInfo[];
    }

    getData(si: TradingSetRelationSearchInput, o?: ICRUDOptions): Observable<DataResult> {
        return super.getData(si, o);
    }

    async deleteItem(id: string): Promise<DeletionResponse> {

        let res = await this.baseDataService.mutate<DeleteTradingSetRelationMutation, DeleteTradingSetRelationMutationVariables>(
            DELETE_TRADING_SET_RELATION, {
            id: id
        });
        return (await lastValueFrom(res)).deleteTradingSetRelation;
    }

    async saveItem(tradingSetRelation: TradingSetRelation): Promise<TradingSetRelation> {
        const validated = await this.systemService.validateObject<TradingSetRelationInput>(tradingSetRelation, "TradingSetRelationInput");

        let saved = await lastValueFrom(this.baseDataService.mutate<SaveTradingSetRelationMutation, SaveTradingSetRelationMutationVariables>(
            SAVE_TRADING_SET_RELATION, {
            input: validated,
        }));
        return saved.saveTradingSetRelation as TradingSetRelation;
    }

    async saveItems(items: any[] | TradingSetRelation[]): Promise<any> {

        let validatedItems = [];

        for (let i of items) {
            validatedItems.push(await this.systemService.validateObject<TradingSetRelationInput>(i, "TradingSetRelationInput"));
        }

        return await lastValueFrom(this.baseDataService.mutate<SaveTradingSetRelationsMutation, SaveTradingSetRelationsMutationVariables>(
            SAVE_TRADING_SET_RELATIONS, {
            input: validatedItems,
        }));
    }
}